import { Link } from "react-router-dom";
import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import Image from "../ui/Image";
import imgicon from '../../assets/icons/new-icon.png'
import { useLanguage } from "../../store/language-context";
import { useTranslation } from "react-i18next";

export default function NewsSlider() {
   const { URL } = useLanguage();
   const { t } = useTranslation();
   
   const { data: news, error, isLoading } = useQuery({
      queryKey: ['news-ivory-home', URL],
      queryFn: async () => {
         try {
            const response = await axios.get(`${URL}/latestNews?per_page=400`);
            return response?.data?.data?.data;
         } catch (error) {
            throw new Error(t('custom.err'))
         }
      },
      staleTime: 1000 * 60 * 60 * 10, 
      gcTime: 1000 * 60 * 60 * 10,
   })

   const [currentSlide, setCurrentSlide] = useState(0);
   // const totalSlides = news?.length;

   const nextSlide = () => {
      setCurrentSlide((prev) => {
         const newSlide = (prev + 1) % Math.ceil(news?.length / 3);
         // console.log("Next slide:", newSlide);
         return newSlide;
      });
   };

   const prevSlide = () => {
      setCurrentSlide((prev) => {
         const newSlide = prev === 0 ? Math.ceil(news?.length / 3) - 1 : prev - 1;
         // console.log("Prev slide:", newSlide);
         return newSlide;
      });
   };

   const firstNews = news?.[0];

   if (isLoading) {
      return (
         <div className="container">
            <div className=" py-4 grid grid-cols-2 gap-4 max-lg:grid-cols-1 max-lg:px-4 w-full h-[32rem]">
               <div className="w-full h-full space-y-2">
                  <div className="w-full rounded-lg l-load h-72"></div>
                  <div className="flex items-center gap-2 w-full">
                     <div className="size-8 rounded-full l-load-dt"></div>
                     <div className="w-3/4 h-4 rounded-lg l-load-dt"></div>
                  </div>
               </div>
               <div className="w-full h-full space-y-2">
                  {
                     [...Array(3)].map((_, i) => (
                        <div key={i} className="flex items-start gap-3">
                           <div className="l-load w-52 rounded-lg h-[5.7rem]"></div>
                           <div className="w-full flex items-center gap-3">
                              <div className="size-8 rounded-full l-load-dt"></div>
                              <div className="w-3/4 h-5 rounded-lg l-load-dt"></div>
                           </div>
                        </div>
                     ))
                  }
               </div>
            </div>
         </div>
      )
   };

   return (
      <section className="bg-white text-gray-900 two-slider">
         <div className=" py-10">
            <div className="text-center mb-4">
               <h2 className="section-title">{t('home.ivory-news')}</h2>
            </div>
            {
               error ?
                  <p className="text-center text-lg text-red-600">
                     {error?.message}
                  </p>
                  :
                  <>
                     <div className="two-slider grid grid-cols-[46%_46%] gap-4 max-xl:grid-cols-1 grid-rows-1">
                        {/* Right Side (Featured News) */}
                        {news?.length > 0 && (
                           <div className="only-one p-0">
                              <div className="h-100">
                                 <div className="relative w-full h-64">
                                    <Image src={news.at(-1)?.image} alt="Featured News" className={'w-full h-full object-cover rounded-lg'} />
                                    <div className="overlay-img"></div>
                                 </div>
                                 <div className="pe-3 ps-3">
                                    <b className="">
                                       <span className="relative">
                                          <Image src={imgicon} alt="icon" />
                                       </span> {news.at(-1)?.date}
                                    </b>
                                    <h3 className="">{news.at(-1)?.title}</h3>
                                    <p className="text-sm">{news.at(-1)?.excerpt}</p>
                                    {news.at(-1)?.id && (
                                       <Link to={`/news/${news.at(-1)?.id}`} className="text-orange-500 text-sm inline-block">
                                          {t('custom.more-details')} <span>←</span>
                                       </Link>
                                    )}
                                 </div>
                              </div>
                           </div>
                        )}


                        {/* Left Side (Small News Items) */}
                        <div className="p-0 three-cards">
                           {news?.length > 0 && (
                              news?.slice(currentSlide * 3, currentSlide * 3 + 3)?.map((item) => (
                                 <div key={item?.id} className="items-center item p-0">
                                    <div className="relative p-0 col-lg-4 w-60 h-auto">
                                       <Image src={item.image} alt="new-image" className="rounded-md p-0 object-cover w-full h-full" />
                                       <div className="overlay-img"></div>
                                    </div>
                                    <div className="text-point">
                                       <b className="text-xs">
                                          <span><img src={imgicon} alt="icon" /></span>{item?.date}
                                       </b>
                                       <h3>{item?.title}</h3>
                                       <p className="text-xs">{item?.excerpt}</p>
                                       <a href={`/news/${item?.id}`} className="">
                                          {t('custom.more-details')} <span>←</span>
                                       </a>
                                    </div>
                                 </div>
                              ))
                           )}
                        </div>
                     </div>


                     {/* Navigation Buttons */}
                     <div className="flex justify-center space-x-4">
                        <button onClick={prevSlide} disabled={news?.length === 0} className="prev">
                           <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button onClick={nextSlide} disabled={news?.length === 0} className="next">
                           <ChevronRight className="h-4 w-4" />
                        </button>
                     </div>
                  </>
            }
         </div>
         <Link to="/all-news" className="flex items-center justify-center mt-4 bg-secondary-tw text-white px-4 py-2 rounded-md hover:bg-secondary-tw/80 transition-colors text-center fs-6"
              style={{width:'200px',margin:'20px auto'}}
             >
                 {t('see-all-news')}
               </Link>
      </section>
   );
}
